import { useMemo, useState } from "react";
import { Plus, Minus, Pencil, Trash2, X, Check, Loader2, ChefHat, Sparkles } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { compatibleUnits, toBase } from "../lib/units";

const CATEGORIES = ["Starters","Mains","Desserts","Beverages","Breads","Sides","Combos"];
const GST_RATES = [0, 5, 12, 18, 28];

function AiFill({ name, setName, onResult }) {
  const [servings, setServings] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const go = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/recipes/ai-suggest?branch=${window.__fd_branch}`, { name, servings: Number(servings) || 0, notes });
      onResult(data);
      setMsg({ ok: true, t: `AI added ${data.ingredients.length} ingredients — ${data.matched} matched to your inventory, ${data.estimated} at estimated prices. Review everything below, then Save.` });
    } catch (e) { setMsg({ t: errorMessage(e) }); } finally { setBusy(false); }
  };
  return (
    <div className="ai-fill" data-testid="ai-fill">
      <div className="form-grid">
        <label>Dish to create<input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Paneer Butter Masala, restaurant style" data-testid="ai-dish-name"/></label>
        <label>Servings per batch (optional)<input type="number" min="1" value={servings} onChange={e => setServings(e.target.value)} placeholder="AI decides" data-testid="ai-servings"/></label>
        <label style={{ gridColumn: "1 / -1" }}>Notes for the AI (optional)<input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Jain, no onion-garlic · less spicy · use Amul butter" data-testid="ai-notes"/></label>
      </div>
      <button type="button" className="primary-button" disabled={busy || name.trim().length < 2} onClick={go} data-testid="ai-generate">{busy ? <><Loader2 className="spin" size={14}/> AI is writing the recipe…</> : <><Sparkles size={14}/> Auto-fill ingredients &amp; costing</>}</button>
      {msg && <div className={`auth-alert ${msg.ok ? "info" : "error"}`} data-testid="ai-message">{msg.t}</div>}
    </div>
  );
}

function RecipeForm({ initial, inventory, onCancel, onSave, busy, error }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    category: (initial?.category || "Mains").toUpperCase(),
    department: initial?.department || "",
    yield_servings: initial?.yield_servings || 1,
    selling_price: initial?.selling_price ? String(initial.selling_price) : "",
    wastage: initial?.wastage ?? 3,
    gst_rate: initial?.gst_rate ?? 5,
    hsn_sac: initial?.hsn_sac || "2106",
    notes: initial?.notes || "",
    ingredients: initial?.ingredients || [],
  }));

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addIng = () => setForm(f => ({ ...f, ingredients: [...f.ingredients, { ingredient_id: "", qty: "", unit: "" }] }));
  const updIng = (i, patch) => setForm(f => ({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const rmIng = (i) => setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const invOf = (it) => inventory.find(x => x.id === it.ingredient_id);
  const pickIngredient = (i, ingredient_id) => {
    const inv = inventory.find(x => x.id === ingredient_id);
    updIng(i, inv ? { ingredient_id: inv.id, unit: compatibleUnits(inv.unit).includes(form.ingredients[i].unit) ? form.ingredients[i].unit : inv.unit } : { ingredient_id: "" });
  };
  const lineCost = (it) => { const inv = invOf(it); return inv ? toBase(it.qty, it.unit || inv.unit, inv.unit) * Number(inv.unit_cost || 0) : it.estimated ? (Number(it.qty) || 0) * (Number(it.unit_cost) || 0) : 0; };
  const [mode, setMode] = useState("manual");
  const problems = form.ingredients.map((it, i) => {
    const inv = invOf(it);
    if (!inv && it.estimated) return it.name?.trim() && Number(it.qty) > 0 ? "" : `Line ${i + 1}: estimated ingredient needs a name and quantity`;
    if (!inv) return `Line ${i + 1}: pick an ingredient from inventory`;
    if (!(Number(it.qty) > 0)) return `Line ${i + 1}: quantity for ${inv.name} must be more than 0`;
    if (!compatibleUnits(inv.unit).includes(it.unit || inv.unit)) return `Line ${i + 1}: ${it.unit} can't be converted to ${inv.unit}`;
    return "";
  }).filter(Boolean);

  const subtotal = form.ingredients.reduce((a, b) => a + lineCost(b), 0);
  const costPerBatch = subtotal * (1 + (Number(form.wastage) || 0) / 100);
  const costPerServing = costPerBatch / Math.max(1, Number(form.yield_servings) || 1);
  const foodCostPct = form.selling_price > 0 ? (costPerServing / Number(form.selling_price)) * 100 : 0;

  const applyAi = (d) => setForm(f => ({ ...f, name: f.name || d.name, category: d.category, yield_servings: d.yield_servings, wastage: d.wastage,
    selling_price: f.selling_price === "" || Number(f.selling_price) === 0 ? String(d.selling_price || "") : f.selling_price, notes: f.notes || d.notes, ingredients: d.ingredients }));
  const estCount = form.ingredients.filter(x => x.estimated && !invOf(x)).length;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!problems.length) onSave(form); }}>
      <div className="ai-mode" role="tablist" data-testid="recipe-mode">
        <button type="button" className={mode === "manual" ? "on" : ""} onClick={() => setMode("manual")} data-testid="recipe-mode-manual"><Pencil size={13}/> Manual</button>
        <button type="button" className={mode === "ai" ? "on" : ""} onClick={() => setMode("ai")} data-testid="recipe-mode-ai"><Sparkles size={13}/> AI auto-fill</button>
      </div>
      {mode === "ai" && <AiFill name={form.name} setName={(v) => upd("name", v)} onResult={applyAi} />}
      <div className="form-grid">
        <label>Dish name<input value={form.name} onChange={e => upd("name", e.target.value)} required placeholder="e.g. Paneer Butter Masala" data-testid="recipe-name-input"/></label>
        <label>Category<select value={form.category} onChange={e => upd("category", e.target.value)} data-testid="recipe-category">{[...new Set([...CATEGORIES.map(c => c.toUpperCase()), form.category])].map(c => <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>)}</select></label>
        <label>P&amp;L department<select value={form.department} onChange={e => upd("department", e.target.value)} data-testid="recipe-department"><option value="">Auto (from category)</option><option value="food">FOODING</option><option value="beverage">BEVERAGES</option></select></label>
        <label>Yield / servings per batch<input type="number" min="1" step="1" value={form.yield_servings} onChange={e => upd("yield_servings", e.target.value)} data-testid="recipe-yield-input"/></label>
        <label>Selling price (₹ per serving)<input type="number" step="0.01" value={form.selling_price} onChange={e => upd("selling_price", e.target.value)} placeholder="0" data-testid="recipe-price-input"/></label>
        <label>Wastage %<input type="number" step="0.1" min="0" max="99" value={form.wastage} onChange={e => upd("wastage", e.target.value)} data-testid="recipe-wastage-input"/></label>
        <label>GST %<select value={form.gst_rate} onChange={e => upd("gst_rate", Number(e.target.value))}>{GST_RATES.map(r => <option key={r} value={r}>{`${r}%`}</option>)}</select></label>
        <label>HSN / SAC<input value={form.hsn_sac} onChange={e => upd("hsn_sac", e.target.value)}/></label>
      </div>

      <div className="recipe-ing-head">
        <span className="overline">INGREDIENTS USED PER BATCH</span>
        <button type="button" className="secondary-button" onClick={addIng} data-testid="add-ingredient-row"><Plus size={13}/> Add ingredient</button>
      </div>
      {form.ingredients.length === 0 && <div className="empty small">No ingredients yet. Add some from your inventory to compute cost per plate.</div>}
      <div className="recipe-ing-table">
        {form.ingredients.map((it, i) => (
          <div className="recipe-ing-row" key={i} data-testid={`ingredient-row-${i}`}>
            <select className={it.estimated && !invOf(it) ? "ing-est" : ""} value={invOf(it) ? it.ingredient_id : ""} onChange={e => pickIngredient(i, e.target.value)} data-testid={`ingredient-picker-${i}`} style={{gridColumn:"span 2"}} title={it.estimated && !invOf(it) ? "Estimated price — pick the matching inventory item to use your own cost and deduct stock" : undefined}>
              <option value="">{it.estimated && !invOf(it) ? `${it.name} · ESTIMATED (not in inventory)` : it.name && !invOf(it) ? `⚠ ${it.name} (not in inventory) — pick again` : "— Pick from inventory —"}</option>
              {inventory.filter(x => !x.archived).map(x => <option key={x.id} value={x.id}>{x.name} ({x.unit}) — {money(x.unit_cost)}/{x.unit}</option>)}
            </select>
            <input type="number" step="0.001" min="0" placeholder="Qty" value={it.qty} onChange={e => updIng(i, { qty: e.target.value })} data-testid={`ingredient-qty-${i}`}/>
            <select value={it.unit || invOf(it)?.unit || ""} onChange={e => updIng(i, { unit: e.target.value })} disabled={!invOf(it)} data-testid={`ingredient-unit-${i}`}>{(invOf(it) ? compatibleUnits(invOf(it).unit) : [it.unit || "—"]).map(u => <option key={u}>{u}</option>)}</select>
            {invOf(it) ? <small className="muted-label" title="Inventory rate">{`${money(invOf(it).unit_cost)}/${invOf(it).unit}`}{it.ai && <em className="ing-ai-tag"> AI</em>}</small>
              : it.estimated ? <label className="ing-est-price" title="Estimated market price per recipe unit — edit to your own"><input type="number" min="0" step="0.0001" value={it.unit_cost} onChange={e => updIng(i, { unit_cost: e.target.value })} data-testid={`ingredient-est-cost-${i}`}/><small>₹/{it.unit} est.</small></label>
              : <small className="muted-label">—</small>}
            <span className="ing-line-cost" data-testid={`ingredient-line-cost-${i}`}>{money(lineCost(it))}</span>
            <button type="button" className="icon-button" onClick={() => rmIng(i)} data-testid={`remove-ingredient-${i}`}><X size={14}/></button>
          </div>
        ))}
      </div>

      <div className="recipe-costing-summary">
        <div><small>Ingredient subtotal</small><b>{money(subtotal)}</b></div>
        <div><small>+ Wastage ({form.wastage}%)</small><b>{money(costPerBatch - subtotal)}</b></div>
        <div><small>Cost per batch</small><b>{money(costPerBatch)}</b></div>
        <div className="hilight"><small>Cost per serving</small><b data-testid="cost-per-serving">{money(costPerServing)}</b></div>
        <div className={foodCostPct && foodCostPct < 30 ? "good" : "warn"}><small>Food cost %</small><b data-testid="food-cost-pct">{foodCostPct.toFixed(1)}%</b></div>
        <div><small>Margin / serving</small><b data-testid="margin-per-serving">{money(Number(form.selling_price) - costPerServing)}</b></div>
      </div>
      {estCount > 0 && <p className="ing-est-note" data-testid="recipe-estimated-note">{estCount} ingredient{estCount > 1 ? "s use" : " uses"} an ESTIMATED market price (highlighted). Edit the price or pick the matching inventory item; estimated items don't deduct stock.</p>}
      {form.ingredients.length === 0 && <p className="form-hint" data-testid="recipe-no-ingredients-hint">No ingredients mapped — cost will be ₹0 and stock won't be deducted on sale.</p>}
      {problems.length > 0 && <div className="auth-alert error" data-testid="recipe-validation">{problems.map(p => <div key={p}>{p}</div>)}</div>}

      <label>Prep notes<textarea rows={2} value={form.notes} onChange={e => upd("notes", e.target.value)} placeholder="Optional prep instructions"/></label>

      {error && <div className="auth-alert error">{error}</div>}
      <div className="setup-actions" style={{marginTop:14}}>
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <span style={{flex:1}}/>
        <button className="primary-button" disabled={busy || !form.name.trim() || problems.length > 0} data-testid="save-recipe-button">{busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Save recipe</button>
      </div>
    </form>
  );
}

export function RecipeModal({ open, onClose, initial, inventory, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const save = async (form) => {
    setBusy(true); setError("");
    try {
      const payload = {
        name: form.name, category: form.category, department: form.department,
        yield_servings: Number(form.yield_servings) || 1,
        selling_price: Number(form.selling_price) || 0,
        wastage: Number(form.wastage) || 0,
        gst_rate: Number(form.gst_rate ?? 5),
        hsn_sac: form.hsn_sac || "2106",
        notes: form.notes || "", available: initial?.available !== false,
        ingredients: form.ingredients.map(i => i.estimated && !i.ingredient_id
          ? { ingredient_id: "", name: i.name, unit: i.unit, qty: Number(i.qty) || 0, unit_cost: Number(i.unit_cost) || 0, estimated: true, est_price_label: i.est_price_label || "", ai: !!i.ai }
          : { ingredient_id: i.ingredient_id || "", unit: i.unit, qty: Number(i.qty) || 0 }),
      };
      if (initial?.id) {
        const { data } = await api.put(`/recipes/${initial.id}`, payload);
        onSaved?.(data);
      } else {
        const { data } = await api.post(`/recipes?branch=${window.__fd_branch}`, payload);
        onSaved?.(data);
      }
      onClose();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal recipe-modal modal-xwide" data-testid="recipe-modal">
        <div className="modal-head">
          <div><span className="overline">RECIPE &amp; COSTING</span><h2>{initial?.id ? "Edit dish" : "Create dish"}</h2></div>
          <button className="icon-button" onClick={onClose}><X size={18}/></button>
        </div>
        <RecipeForm initial={initial} inventory={inventory} onCancel={onClose} onSave={save} busy={busy} error={error}/>
      </div>
    </div>
  );
}

export default function RecipesView({ recipes, inventory, branch, onChanged }) {
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  // Make branch available to the modal
  window.__fd_branch = branch;

  const grouped = useMemo(() => {
    const g = {};
    for (const r of recipes) { (g[r.category] = g[r.category] || []).push(r); }
    return g;
  }, [recipes]);

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/recipes/${confirmDelete.id}`); setConfirmDelete(null); onChanged?.(); }
    catch (e) { alert(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="recipes-head">
        <div><span className="overline">RECIPE MASTER</span><h2 style={{fontSize:20,margin:"6px 0 2px"}}>Dish recipes &amp; costing</h2><p style={{margin:0,color:"var(--muted)",fontSize:13}}>Set the ingredients used per batch — FynDine computes the cost per plate and food-cost percentage.</p></div>
        <button className="primary-button" onClick={() => setModal({ open: true, initial: null })} data-testid="add-recipe-button"><Plus size={16}/> New dish</button>
      </div>

      {Object.keys(grouped).length === 0 && <div className="panel" style={{padding:36,textAlign:"center",color:"var(--muted)"}} data-testid="recipes-empty">No dishes yet. Create one and add the ingredients used per batch — e.g. a plate of Paneer Butter Masala with paneer, tomato, oil, spices.</div>}

      {Object.entries(grouped).map(([cat, list]) => (
        <section key={cat} style={{marginBottom:18}}>
          <span className="overline" style={{display:"block",margin:"8px 0 10px"}}>{cat}</span>
          <div className="recipe-grid">
            {list.map(r => {
              const cps = r.cost_per_serving ?? r.cost / Math.max(1, r.yield_servings);
              const pct = r.selling_price > 0 ? (cps / r.selling_price) * 100 : 0;
              return (
                <article className="recipe-card detailed" key={r.id} data-testid={`recipe-card-${r.id}`}>
                  <div className="recipe-top">
                    <div><ChefHat size={14} color="#f0a768"/> <span className="category-label">{r.category}</span></div>
                    <div style={{display:"flex",gap:4}}>
                      <button className="icon-button" title="Edit" onClick={() => setModal({ open: true, initial: r })} data-testid={`edit-recipe-${r.id}`}><Pencil size={14}/></button>
                      <button className="icon-button" title="Delete" onClick={() => setConfirmDelete(r)} data-testid={`delete-recipe-${r.id}`}><Trash2 size={14}/></button>
                    </div>
                  </div>
                  <h3>{r.name}</h3>
                  <span className="muted-label">Yields {r.yield_servings} · Wastage {r.wastage}% · GST {r.gst_rate ?? 5}%</span>

                  <div className="recipe-ing-list">
                    {(r.ingredients || []).length === 0 && <div className="empty small">No ingredients mapped yet. Click Edit to add them.</div>}
                    {(r.ingredients || []).map((it, i) => (
                      <div key={i} className="recipe-ing-item">
                        <span>{it.name}</span>
                        <small>{it.qty} {it.unit}</small>
                        <b className={(it.unmapped && !it.estimated) || it.unit_mismatch ? "orange-text" : ""} title={it.estimated ? "Uses an estimated market price — link to inventory for your own cost" : it.unmapped ? "Not linked to inventory — edit to fix" : it.unit_mismatch ? "Unit can't convert to inventory unit" : ""}>{(it.unmapped && !it.estimated) || it.unit_mismatch ? "⚠ " : ""}{money(it.line_cost ?? Number(it.qty) * Number(it.unit_cost))}{it.estimated && <em className="ing-est-tag" data-testid="recipe-line-est-tag"> est.</em>}</b>
                      </div>
                    ))}
                  </div>

                  <div className="cost-line">
                    <div><small>Cost / serving</small><strong>{money(cps)}</strong></div>
                    <div><small>Food cost</small><strong className={pct < 30 ? "green-text" : "orange-text"}>{pct.toFixed(1)}%</strong></div>
                    <div><small>Selling price</small><strong>{money(r.selling_price)}</strong></div>
                  </div>
                  <div className="recipe-foot"><span>Batch cost <b>{money(r.cost)}</b></span><span className="margin" data-testid={`recipe-margin-${r.id}`}>Margin {money(r.selling_price - cps)}</span></div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <RecipeModal
        open={!!modal}
        initial={modal?.initial}
        inventory={inventory}
        onClose={() => setModal(null)}
        onSaved={() => onChanged?.()}
      />

      {confirmDelete && (
        <div className="modal-backdrop">
          <div className="modal" data-testid="recipe-delete-modal">
            <div className="modal-head"><div><span className="overline">CONFIRM</span><h2>Delete this dish?</h2></div><button className="icon-button" onClick={() => setConfirmDelete(null)}><X size={18}/></button></div>
            <p style={{color:"var(--muted)",fontSize:13}}>“{confirmDelete.name}” will be removed from the menu and POS. This cannot be undone.</p>
            <div className="setup-actions" style={{marginTop:14}}>
              <button className="secondary-button" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <span style={{flex:1}}/>
              <button className="primary-button" style={{background:"#8a2a2a"}} disabled={busy} onClick={doDelete} data-testid="confirm-delete-recipe"><Trash2 size={14}/> Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
